import { addDays } from "date-fns";

import { convertToJST, DateFormat, format, isInvalidDate } from "@/utils/date";

import type {
  ReferralNoticeSurface,
  ReferralNoticeViewModel,
  ReferralOverview,
  RewardEvent,
} from "./types";

export const REFERRAL_REWARD_EVENT_TYPE = {
  FREE_SIGNUP_BONUS: "free_signup_bonus",
  PAID_CONVERSION: "paid_conversion",
  MILESTONE_BONUS: "milestone_bonus",
  REFEREE_REWARD: "referee_reward",
} as const;

export const REFERRAL_NOTICE_SURFACE = {
  MYPAGE: "mypage",
  PLAN: "plan",
} as const;

export const REFERRAL_NOTICE_TONE = {
  TEAL: "teal",
  GOLD: "gold",
} as const;

export const REFERRAL_NOTICE_ICON = {
  HEART: "heart",
  CROWN: "crown",
} as const;

export const REFERRAL_NOTICE_KIND = {
  REWARD: "reward",
  ELIGIBILITY: "eligibility",
} as const;

const DISMISSED_EVENT_KEY_PREFIX = "yoyacoo.referral.dismissed-reward.";

export const getReferralDismissalKey = (eventId: string) =>
  `${DISMISSED_EVENT_KEY_PREFIX}${eventId}`;

type ReferralDismissalReader = Pick<Storage, "getItem">;
type ReferralDismissalWriter = Pick<Storage, "setItem">;

export const getReferralDismissalState = (
  storage: ReferralDismissalReader,
  eventId: string,
) => {
  try {
    return storage.getItem(getReferralDismissalKey(eventId)) === "true";
  } catch {
    return false;
  }
};

export const saveReferralDismissalState = (
  storage: ReferralDismissalWriter,
  eventId: string,
) => {
  try {
    storage.setItem(getReferralDismissalKey(eventId), "true");
    return true;
  } catch {
    return false;
  }
};

export const isReferralNoticeDismissed = (
  eventId: string | null,
  dismissedEventId: string | null,
) => eventId !== null && eventId === dismissedEventId;

const formatJapaneseDate = (value: string): string | null => {
  const date = new Date(value);
  if (isInvalidDate(date)) return null;

  return format(convertToJST(date), DateFormat.DATE_JP);
};

const formatPlanRenewalDate = (
  billingUseEndDate: string | undefined,
): string | null => {
  if (!billingUseEndDate) return null;

  const date = new Date(billingUseEndDate);
  if (isInvalidDate(date)) return null;

  return formatJapaneseDate(addDays(date, 1).toISOString());
};

const rewardCopy = (
  event: RewardEvent,
  surface: ReferralNoticeSurface,
  planBillingUseEndDate?: string,
  paidMilestoneTotalMonths?: number,
  isFreeOrTrialPlan?: boolean,
): Pick<ReferralNoticeViewModel, "title" | "body" | "tone" | "icon"> | null => {
  // Account plan determines copy. appliedAt is only a hydration fallback.
  const pending = isFreeOrTrialPlan ?? event.appliedAt === null;
  const displayMonths =
    pending &&
    event.type === REFERRAL_REWARD_EVENT_TYPE.MILESTONE_BONUS &&
    typeof paidMilestoneTotalMonths === "number" &&
    paidMilestoneTotalMonths > 0
      ? paidMilestoneTotalMonths
      : event.months;
  const months = `${displayMonths}ヶ月`;
  const nextBillingDate =
    surface === REFERRAL_NOTICE_SURFACE.PLAN
      ? formatPlanRenewalDate(planBillingUseEndDate) ??
        (event.nextBillingDate
          ? formatJapaneseDate(event.nextBillingDate)
          : null)
      : null;
  const planExtension = nextBillingDate
    ? `ご利用期間・次回更新日を延長しました。（${nextBillingDate}）`
    : null;
  const earnedFreeExtension = `ご利用期間を${months}無料延長を獲得しました。`;

  switch (event.type) {
    case REFERRAL_REWARD_EVENT_TYPE.FREE_SIGNUP_BONUS:
      return {
        title: "紹介ボーナス獲得",
        tone: REFERRAL_NOTICE_TONE.TEAL,
        icon: REFERRAL_NOTICE_ICON.HEART,
        body: pending
          ? `あなたの紹介で3名のユーザーがヨヤクーに登録しました。あなたが有料プランにアップグレードすると、ご利用期間を無料で${months}延長します。`
          : `あなたの紹介で3名のユーザーがヨヤクーに登録しました。ご紹介ありがとうございます。特典として${
              planExtension ?? earnedFreeExtension
            }`,
      };
    case REFERRAL_REWARD_EVENT_TYPE.PAID_CONVERSION:
      return {
        title: "有料化ボーナス獲得",
        tone: REFERRAL_NOTICE_TONE.GOLD,
        icon: REFERRAL_NOTICE_ICON.CROWN,
        body: pending
          ? `あなたの紹介ユーザーが有料プランにアップグレードしました。あなたが有料プランにアップグレードすると、ご利用期間を無料で${months}延長します。`
          : `あなたの紹介ユーザーが有料プランにアップグレードしました。ご紹介ありがとうございます。特典として${
              planExtension ?? earnedFreeExtension
            }`,
      };
    case REFERRAL_REWARD_EVENT_TYPE.MILESTONE_BONUS:
      return {
        title: "有料化ボーナス5名達成",
        tone: REFERRAL_NOTICE_TONE.GOLD,
        icon: REFERRAL_NOTICE_ICON.CROWN,
        body: pending
          ? `あなたの紹介で5名が有料プランにアップグレードしました。あなたが有料プランにアップグレードすると、ご利用期間を無料で${months}延長します。`
          : `あなたの紹介で5名が有料プランにアップグレードしました。特別な5名有料化ボーナスを適用し、${
              planExtension
                ? `${planExtension}。`
                : "ご利用期間を延長しました。"
            }`,
      };
    case REFERRAL_REWARD_EVENT_TYPE.REFEREE_REWARD:
      return {
        title: "ご紹介特典",
        tone: REFERRAL_NOTICE_TONE.TEAL,
        icon: REFERRAL_NOTICE_ICON.HEART,
        body: pending
          ? `紹介特典として、ご利用期間を無料で${months}延長する特典を獲得しました。`
          : `紹介特典として、${planExtension ?? earnedFreeExtension}`,
      };
    default:
      return null;
  }
};

type NoticeOverview = Pick<
  ReferralOverview,
  "latestRewardEvent" | "refereeRewardEligibleUntil"
> & {
  stats: Pick<ReferralOverview["stats"], "paidMilestoneTotalMonths">;
};

export const selectReferralNotice = (
  overview: NoticeOverview,
  surface: ReferralNoticeSurface,
  planBillingUseEndDate?: string,
  isFreeOrTrialPlan?: boolean,
): ReferralNoticeViewModel | null => {
  if (overview.latestRewardEvent) {
    const event = overview.latestRewardEvent;
    const copy = rewardCopy(
      event,
      surface,
      planBillingUseEndDate,
      overview.stats.paidMilestoneTotalMonths,
      isFreeOrTrialPlan,
    );
    if (!copy) return null;

    return {
      kind: REFERRAL_NOTICE_KIND.REWARD,
      ...copy,
      eventId: event.id,
      dismissible: true,
      cta:
        surface === REFERRAL_NOTICE_SURFACE.MYPAGE
          ? { label: "詳しく見る", href: "/accounts/plan?tab=1" }
          : null,
    };
  }

  if (!overview.refereeRewardEligibleUntil) return null;

  const deadline = formatJapaneseDate(overview.refereeRewardEligibleUntil);
  if (!deadline) return null;

  return {
    kind: REFERRAL_NOTICE_KIND.ELIGIBILITY,
    title: "ご紹介特典",
    body: `${deadline}までに有料プランへアップグレードしていただくと、利用期間の1ヶ月分無料延長の特典を獲得できます。`,
    tone: REFERRAL_NOTICE_TONE.TEAL,
    icon: REFERRAL_NOTICE_ICON.HEART,
    eventId: null,
    dismissible: false,
    cta:
      surface === REFERRAL_NOTICE_SURFACE.MYPAGE
        ? {
            label: "アップグレードはこちら",
            href: "/accounts/plan?tab=2",
          }
        : null,
  };
};
